import { Pencil } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { fmtMin } from "@/lib/weeks";

const KIND_STYLE = {
  red: "bg-red-100 text-red-700", sky: "bg-sky-100 text-sky-700", amber: "bg-amber-100 text-amber-800",
  violet: "bg-violet-100 text-violet-700", emerald: "bg-emerald-100 text-emerald-700", slate: "bg-slate-100 text-slate-700",
};

const cell = "py-2 px-2 whitespace-nowrap";
const num = `${cell} font-mono-num text-right`;

const rowTone = (d) => (d.is_holiday ? "bg-orange-100" : d.is_sunday ? "bg-red-50" : "");

const DayRow = ({ d, canEdit, onEdit }) => (
  <tr data-testid={`ts-day-${d.date}`} data-holiday={d.is_holiday} data-sunday={d.is_sunday} className={`border-t border-gray-100 ${rowTone(d)}`}>
    <td className={`${cell} font-mono-num text-xs`}>{fmtDate(d.date)}</td>
    <td className={`${cell} text-xs font-semibold ${d.is_sunday || d.is_holiday ? "text-red-700" : "text-gray-600"}`}>{d.weekday_label}</td>
    <td className={`${cell} font-mono-num`}>{d.clock_in || "—"}</td>
    <td className={`${cell} font-mono-num`}>{d.clock_out || "—"}</td>
    <td className={num}>{d.break_minutes ? fmtMin(d.break_minutes) : ""}</td>
    <td className={num}>{d.contract_minutes ? fmtMin(d.contract_minutes) : ""}</td>
    <td className={`${num} font-semibold`}>{d.worked_minutes ? fmtMin(d.worked_minutes) : ""}</td>
    <td className={`${num} ${d.overtime_minutes ? "text-emerald-700 font-semibold" : ""}`}>{d.overtime_minutes ? fmtMin(d.overtime_minutes) : ""}</td>
    <td className="py-2 px-2">
      <div className="flex flex-wrap gap-1">
        {d.absences.map((a) => (
          <span key={a.id} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${KIND_STYLE[a.color] || KIND_STYLE.slate}`} title={a.reason}>
            {a.label}{a.hours ? ` ${a.hours}h` : ""}
          </span>
        ))}
        {d.is_holiday && d.absences.length === 0 && <span className="text-xs text-orange-800 font-medium">Festivo · {d.holiday_name}</span>}
      </div>
    </td>
    <td className="py-2 px-2 text-xs text-gray-600 max-w-[260px] truncate" title={[d.note, d.attendance_notes].filter(Boolean).join("; ")}>
      {[d.note, d.attendance_notes].filter(Boolean).join("; ")}
    </td>
    {canEdit && (
      <td className="py-2 px-2 text-right">
        <button data-testid={`ts-edit-${d.date}`} onClick={onEdit} aria-label={`Modifica ${fmtDate(d.date)}`}
          className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </td>
    )}
  </tr>
);

const WeekRow = ({ w, cols }) => (
  <tr data-testid={`ts-week-total-${w.week}`} className="bg-emerald-50 border-t-2 border-b-2 border-emerald-600 font-semibold text-emerald-900">
    <td className={`${cell} text-xs uppercase tracking-wider`} colSpan={4}>Totale settimana {w.week}</td>
    <td className={num}>{fmtMin(w.break_minutes)}</td>
    <td className={num}>{fmtMin(w.contract_minutes)}</td>
    <td className={num}>{fmtMin(w.worked_minutes)}</td>
    <td className={num}>{fmtMin(w.overtime_minutes)}</td>
    <td className="py-2 px-2 text-xs" colSpan={cols - 8}>Permessi/recuperi: {w.absence_hours}h</td>
  </tr>
);

export const OperatorSheet = ({ op, canEdit, onEditDay }) => {
  const cols = canEdit ? 11 : 10;
  const t = op.totals;
  let wi = 0;
  return (
    <section data-testid={`ts-operator-${op.operator_id}`} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <header className="px-5 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-head text-lg font-bold">{op.operator_name}</h2>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-gray-100 px-3 py-1">Contratto <b className="font-mono-num">{fmtMin(t.contract_minutes)}</b></span>
          <span className="rounded-full bg-emerald-100 text-emerald-800 px-3 py-1">Lavorate <b className="font-mono-num" data-testid={`ts-total-worked-${op.operator_id}`}>{fmtMin(t.worked_minutes)}</b></span>
          <span className="rounded-full bg-sky-100 text-sky-800 px-3 py-1">Straord. <b className="font-mono-num">{fmtMin(t.overtime_minutes)}</b></span>
          <span className="rounded-full bg-amber-100 text-amber-800 px-3 py-1">Pause <b className="font-mono-num">{fmtMin(t.break_minutes)}</b></span>
          {Object.entries(t.absence_days).map(([k, v]) => <span key={k} className="rounded-full bg-red-50 text-red-700 px-3 py-1">{k} <b>{v} gg</b></span>)}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[980px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-gray-600 bg-gray-50">
              <th className={cell}>Data</th><th className={cell}>Giorno</th><th className={cell}>Ingresso</th><th className={cell}>Uscita</th>
              <th className={`${cell} text-right`}>Pausa</th><th className={`${cell} text-right`}>Ore contr.</th><th className={`${cell} text-right`}>Lavorate</th>
              <th className={`${cell} text-right`}>Straord.</th><th className={cell}>Causale assenza</th><th className={cell}>Note titolare</th>
              {canEdit && <th className={cell} />}
            </tr>
          </thead>
          <tbody>
            {op.days.map((d) => {
              const w = op.weeks[wi];
              const showWeek = w && d.date === w.end;
              if (showWeek) wi += 1;
              return [
                <DayRow key={d.date} d={d} canEdit={canEdit} onEdit={() => onEditDay(op, d)} />,
                showWeek ? <WeekRow key={`w-${w.week}`} w={w} cols={cols} /> : null,
              ];
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
